/**
 * ADMIN CONSOLE COMPONENT: SECUENCIA (SIMON SAYS MULTIPLAYER)
 * Panel de administración y consola de operador para la dinámica Secuencia.
 * Permite la configuración de parámetros de partida (longitud, velocidad, temas y patrocinadores),
 * control en tiempo real (proyección, inicio inteligente one-click, reset, toques de emergencia),
 * visualización de telemetría de juego en vivo, gestión de temas con paleta de 6 botones y carga de multimedia,
 * y auditoría de historial de partidas jugadas.
 * 
 * @module components/Admin/Games/Sequence
 */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { 
    StartSequence, 
    ResetSequence, 
    GetState, 
    ShowFrontPage, 
    GetSequenceHistory, 
    ClearSequenceHistory, 
    RegisterTap,
    ProjectGame, 
    GetSequenceThemes, 
    GetSequenceTheme, 
    SaveSequenceTheme, 
    DeleteSequenceTheme, 
    GetSequenceConfig, 
    SaveSequenceConfig, 
    UploadSequenceMedia 
} from '../../../utilities/BL/Sequence';
import { 
    ConnectWebSocketForSequenceAdmin, 
    DisconnectWebSocketForSequenceAdmin 
} from '../../../utilities/WS/Admin/SequenceWS';
import { 
    playPadTone, 
    playVictoryFanfare, 
    playErrorTone 
} from '../../../utilities/Commons/SequenceAudioSynth';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { PremiumQR } from './../../Commons/PremiumQR';
import { Confirm } from './../../Commons/Modals/Confirm';

/**
 * Componente principal de administración para la dinámica Secuencia.
 * 
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.getOutOfTheDynamic - Función de callback para retornar al Dashboard principal.
 * @param {string|number|null} [props.roomId=null] - Identificador opcional de la sala SaaS.
 * @returns {JSX.Element} Vista renderizada del panel de administración.
 */
const Sequence = ({ getOutOfTheDynamic, roomId = null }) => {
    const { showMessage } = useFloatMessage();
    const [screenId, setScreenId] = useState(1);
    const [sequenceLength, setSequenceLength] = useState(7);
    const [playbackSpeedMs, setPlaybackSpeedMs] = useState(500);

    const [gameState, setGameState] = useState({
        phase: 'WAITING',
        sequence: [],
        playerStep: 0,
        isPlaying: false,
        matchId: '',
        buttons: []
    });

    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [history, setHistory] = useState([]);
    
    // Temas y Configuración
    const [themesList, setThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ message: '', action: null });
    const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);

    // Estado del Formulario del Tema en Edición
    const defaultThemeForm = {
        id: 0,
        name: '',
        buttons: [
            { id: 0, color: '#00ff41', imageUrl: '', label: 'VERDE' },
            { id: 1, color: '#ff003c', imageUrl: '', label: 'ROJO' },
            { id: 2, color: '#fffb00', imageUrl: '', label: 'AMARILLO' },
            { id: 3, color: '#00e1ff', imageUrl: '', label: 'AZUL' },
            { id: 4, color: '#b300ff', imageUrl: '', label: 'MORADO' },
            { id: 5, color: '#ff8800', imageUrl: '', label: 'NARANJA' }
        ]
    };
    const [themeForm, setThemeForm] = useState(defaultThemeForm);
    const [isUploading, setIsUploading] = useState({});

    // Modales QR
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrTargetUrl, setQrTargetUrl] = useState('');
    const [tvTargetUrl, setTvTargetUrl] = useState('');
    const [qrTitle, setQrTitle] = useState('');

    /**
     * Carga el historial de partidas desde la capa de negocio.
     */
    const loadHistory = async () => {
        try {
            const res = await GetSequenceHistory(roomId);
            if (res && res.success && res.data) {
                setHistory(res.data);
            }
        } catch (err) {
            console.error('[SequenceAdmin] Error cargando historial:', err);
        }
    };

    /**
     * Carga la lista de temas visuales disponibles.
     */
    const loadThemes = async () => {
        try {
            const res = await GetSequenceThemes(roomId);
            if (res && res.success && res.data) {
                setThemesList(res.data);
            }
        } catch (err) {
            console.error('[SequenceAdmin] Error cargando temas:', err);
        }
    };

    /**
     * Carga la configuración guardada para la pantalla activa.
     * 
     * @param {number} sId - Identificador numérico de la pantalla.
     */
    const loadScreenConfig = async (sId) => {
        try {
            const res = await GetSequenceConfig(sId, roomId);
            if (res && res.success && res.data) {
                setSequenceLength(res.data.sequenceLength || 7);
                setPlaybackSpeedMs(res.data.playbackSpeedMs || 500);
                setSelectedThemeId(res.data.themeId ? String(res.data.themeId) : '');
            } else {
                setSequenceLength(7);
                setPlaybackSpeedMs(500);
                setSelectedThemeId('');
            }
        } catch (err) {
            console.error('[SequenceAdmin] Error cargando configuración de pantalla:', err);
            setSequenceLength(7);
            setPlaybackSpeedMs(500);
            setSelectedThemeId('');
        }
    };

    /**
     * Auto-guarda los cambios de parámetros en el servidor.
     * 
     * @param {number} sId - Identificador de pantalla.
     * @param {string|number} tId - Identificador del tema.
     * @param {number} len - Longitud de la secuencia.
     * @param {number} speed - Velocidad en milisegundos.
     */
    const autoSaveConfig = async (sId, tId, len, speed) => {
        try {
            await SaveSequenceConfig({
                screenId: sId,
                themeId: tId ? parseInt(tId, 10) : null,
                sequenceLength: len,
                playbackSpeedMs: speed
            }, roomId);
        } catch (err) {
            console.error('[SequenceAdmin] Error en autoSaveConfig:', err);
        }
    };

    /**
     * Consulta el estado actual de la partida activa.
     */
    const fetchCurrentState = async () => {
        try {
            const res = await GetState(screenId, roomId);
            if (res && res.success && res.data) {
                setGameState(res.data);
            }
        } catch (err) {
            console.error('[SequenceAdmin] Error consultando estado actual:', err);
        }
    };

    // Conectar WebSocket / SignalR con SequenceWS
    useEffect(() => {
        const handleStatusChange = (status) => {
            if (status === 'connected') {
                setConnectionStatus('connected');
                setError('');
            } else if (status === 'connecting' || status === 'reconnecting') {
                setConnectionStatus('connecting');
            } else {
                setConnectionStatus('disconnected');
            }
        };

        const handleIncomingMessage = (messageEnvelope) => {
            if (!messageEnvelope) return;
            const rawType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
            const payload = messageEnvelope.payload !== undefined ? messageEnvelope.payload : messageEnvelope.data;

            if (rawType.startsWith('sequence.')) {
                if (payload) {
                    const msgScreenId = payload.screenId !== undefined ? payload.screenId : payload.ScreenId;
                    if (msgScreenId === undefined || msgScreenId === null || Number(msgScreenId) === Number(screenId)) {
                        setGameState(prev => ({
                            ...prev,
                            ...payload
                        }));

                        if (payload.phase === 'WON') {
                            playVictoryFanfare();
                            loadHistory();
                        } else if (payload.phase === 'LOST') {
                            playErrorTone();
                            loadHistory();
                        }
                    }
                }
            }
        };

        ConnectWebSocketForSequenceAdmin(
            handleIncomingMessage,
            handleStatusChange,
            roomId || screenId,
            {
                onLayout: (payload) => {
                    if (payload) {
                        setGameState(prev => ({ ...prev, ...payload }));
                    }
                },
                onUpdate: (payload) => {
                    if (payload) {
                        setGameState(prev => ({ ...prev, ...payload }));
                        if (payload.phase === 'WON') {
                            playVictoryFanfare();
                            loadHistory();
                        } else if (payload.phase === 'LOST') {
                            playErrorTone();
                            loadHistory();
                        }
                    }
                },
                onReset: (payload) => {
                    if (payload) {
                        setGameState(prev => ({
                            ...prev,
                            ...payload,
                            phase: 'WAITING',
                            sequence: [],
                            playerStep: 0,
                            isPlaying: false
                        }));
                    }
                }
            }
        );

        fetchCurrentState();
        loadHistory();
        loadThemes();
        loadScreenConfig(screenId);

        return () => {
            DisconnectWebSocketForSequenceAdmin();
        };
    }, [screenId, roomId]);

    /**
     * Resuelve los botones a partir del tema seleccionado o el fallback por defecto.
     * 
     * @returns {Array<Object>} Lista de 6 pads configurados.
     */
    const customButtonsFromActiveTheme = () => {
        if (selectedThemeId) {
            const activeTheme = themesList.find(t => t.id === parseInt(selectedThemeId, 10));
            if (activeTheme && activeTheme.buttonsJson) {
                try {
                    return typeof activeTheme.buttonsJson === 'string'
                        ? JSON.parse(activeTheme.buttonsJson)
                        : activeTheme.buttonsJson;
                } catch (e) {
                    console.warn('[SequenceAdmin] Error parseando buttonsJson:', e);
                }
            }
        }
        return defaultThemeForm.buttons;
    };

    /**
     * Inicio Directo Inteligente (One-Click Start):
     * Al presionar "Iniciar Juego", despacha inmediatamente los parámetros de juego actuales
     * (longitud, velocidad, tema y botones) sin exigir pasos intermedios ni selección obligatoria previa.
     */
    const handleStart = async () => {
        setIsWorking(true);
        setError('');
        showMessage('Iniciando juego...', MESSAGE_TYPE.INFO);
        
        // Obtener la colección de botones activa o por defecto
        const buttonsToSend = customButtonsFromActiveTheme();

        const payload = {
            screenId: Number(screenId),
            sequenceLength: Number(sequenceLength),
            playbackSpeedMs: Number(playbackSpeedMs),
            themeId: selectedThemeId ? parseInt(selectedThemeId, 10) : null,
            buttons: buttonsToSend
        };

        // Auto-guardado transparente en segundo plano
        autoSaveConfig(screenId, selectedThemeId, sequenceLength, playbackSpeedMs).catch(() => {});

        const res = await StartSequence(payload, roomId);

        if (res && res.success) {
            showMessage('🚀 Secuencia iniciada', MESSAGE_TYPE.SUCCESS);
        } else {
            const errorMsg = res?.message || 'Error al iniciar la dinámica.';
            setError(errorMsg);
            showMessage(errorMsg, MESSAGE_TYPE.ERROR);
        }
        setIsWorking(false);
    };

    /**
     * Reinicia la partida en curso y devuelve el estado a WAITING.
     */
    const handleReset = async () => {
        setIsWorking(true);
        setError('');
        showMessage('Reiniciando...', MESSAGE_TYPE.INFO);
        const res = await ResetSequence(screenId, roomId);
        if (res && res.success) {
            showMessage('🔄 Juego reiniciado', MESSAGE_TYPE.SUCCESS);
            setGameState({
                phase: 'WAITING',
                sequence: [],
                playerStep: 0,
                isPlaying: false,
                matchId: '',
                buttons: []
            });
        } else {
            const errorMsg = res?.message || 'Error al resetear la dinámica.';
            setError(errorMsg);
            showMessage(errorMsg, MESSAGE_TYPE.ERROR);
        }
        setIsWorking(false);
    };

    /**
     * Proyecta el tablero de juego en la pantalla gigante de visualización.
     */
    const handleProjectGameManual = async () => {
        showMessage('Proyectando juego...', MESSAGE_TYPE.INFO);
        const res = await ProjectGame(screenId, roomId);
        if (res && res.success) {
            showMessage('📺 Tablero proyectado en la pantalla', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage(res?.message || 'Error al proyectar', MESSAGE_TYPE.ERROR);
        }
    };

    /**
     * Muestra la portada de la dinámica en la pantalla de estadio.
     */
    const handleShowFrontPageManual = async () => {
        showMessage('Mostrando portada...', MESSAGE_TYPE.INFO);
        const res = await ShowFrontPage(screenId, roomId);
        if (res && res.success) {
            showMessage('📺 Portada mostrada', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage(res?.message || 'Error al mostrar portada', MESSAGE_TYPE.ERROR);
        }
    };

    /**
     * Abre el modal con el código QR y enlaces para TV y dispositivo móvil.
     */
    const openQRModal = () => {
        const roomParam = roomId ? `&roomId=${roomId}` : '';
        const tvUrl = `${window.location.origin}/screen`;
        setTvTargetUrl(tvUrl);

        const playerUrl = `${window.location.origin}/sequence?screenId=${screenId}${roomParam}`;
        setQrTargetUrl(playerUrl);

        setQrTitle(`Secuencia - Pantalla ${screenId}`);
        setShowQRModal(true);
    };

    /**
     * Toque de emergencia desde la consola del operador si falla el dispositivo del jugador.
     * 
     * @param {number} colorId - Índice del pad presionado (0 a 5).
     */
    const handleEmergencyTap = async (colorId) => {
        if (!gameState.isPlaying || gameState.phase !== 'PLAYER_INPUT') {
            showMessage('No es el turno del jugador', MESSAGE_TYPE.WARNING);
            return;
        }
        playPadTone(colorId);
        showMessage(`Toque manual enviado (Botón ${colorId})`, MESSAGE_TYPE.INFO);
        const res = await RegisterTap(screenId, gameState.matchId, colorId, roomId);
        if (!res || !res.success) {
            showMessage(res?.message || 'Error al registrar toque', MESSAGE_TYPE.ERROR);
        }
    };

    /**
     * Muestra confirmación para vaciar el historial de partidas.
     */
    const handleClearHistory = async () => {
        setConfirmConfig({
            message: '¿Estás seguro de eliminar todo el historial de secuencias?',
            action: async () => {
                const res = await ClearSequenceHistory(roomId);
                if (res && res.success) {
                    showMessage('Historial limpio', MESSAGE_TYPE.SUCCESS);
                    loadHistory();
                } else {
                    showMessage(res?.message || 'Error al limpiar historial', MESSAGE_TYPE.ERROR);
                }
            }
        });
        setShowConfirmModal(true);
    };

    // ==========================================
    // GESTIÓN DE TEMAS (MODAL)
    // ==========================================
    const openCreateTheme = () => {
        setThemeForm(defaultThemeForm);
        setShowThemeModal(true);
    };

    const openEditTheme = (theme) => {
        if (!theme) return;
        try {
            const btns = typeof theme.buttonsJson === 'string'
                ? JSON.parse(theme.buttonsJson)
                : theme.buttonsJson;
            setThemeForm({
                id: theme.id,
                name: theme.name,
                buttons: btns || defaultThemeForm.buttons
            });
        } catch {
            setThemeForm({
                id: theme.id,
                name: theme.name,
                buttons: defaultThemeForm.buttons
            });
        }
        setShowThemeModal(true);
    };

    const handleDeleteThemeClick = (themeId) => {
        setConfirmConfig({
            message: '¿Estás seguro de eliminar este tema permanentemente?',
            action: async () => {
                const res = await DeleteSequenceTheme(themeId, roomId);
                if (res && res.success) {
                    showMessage('Tema eliminado', MESSAGE_TYPE.SUCCESS);
                    loadThemes();
                    if (parseInt(selectedThemeId, 10) === themeId) {
                        setSelectedThemeId('');
                        autoSaveConfig(screenId, '', sequenceLength, playbackSpeedMs);
                    }
                } else {
                    showMessage(res?.message || 'Error al eliminar', MESSAGE_TYPE.ERROR);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleSaveTheme = async () => {
        if (!themeForm.name.trim()) {
            showMessage('Por favor ingresa un nombre para el tema.', MESSAGE_TYPE.WARNING);
            return;
        }

        const payload = {
            id: themeForm.id,
            name: themeForm.name.trim(),
            buttonsJson: JSON.stringify(themeForm.buttons)
        };

        showMessage('Guardando tema...', MESSAGE_TYPE.INFO);
        const res = await SaveSequenceTheme(payload, roomId);
        if (res && res.success) {
            showMessage('✅ Tema guardado exitosamente', MESSAGE_TYPE.SUCCESS);
            setShowThemeModal(false);
            loadThemes();
        } else {
            showMessage(res?.message || 'Error al guardar tema', MESSAGE_TYPE.ERROR);
        }
    };

    const handleUploadMediaForButton = async (file, btnId) => {
        setIsUploading(prev => ({ ...prev, [btnId]: true }));
        showMessage('Subiendo imagen...', MESSAGE_TYPE.INFO);
        
        const res = await UploadSequenceMedia(file, roomId);
        if (res && res.success && (res.url || res.data?.url)) {
            const uploadedUrl = res.url || res.data?.url;
            showMessage('✅ Imagen subida', MESSAGE_TYPE.SUCCESS);
            setThemeForm(prev => ({
                ...prev,
                buttons: prev.buttons.map(b => b.id === btnId ? { ...b, imageUrl: uploadedUrl } : b)
            }));
        } else {
            showMessage(res?.message || 'Error al subir imagen', MESSAGE_TYPE.ERROR);
        }
        setIsUploading(prev => ({ ...prev, [btnId]: false }));
    };

    const updateThemeButtonField = (btnId, field, value) => {
        setThemeForm(prev => ({
            ...prev,
            buttons: prev.buttons.map(b => b.id === btnId ? { ...b, [field]: value } : b)
        }));
    };

    // Manejadores de cambios en sliders y selectores
    const handleLengthChange = (val) => {
        setSequenceLength(val);
        autoSaveConfig(screenId, selectedThemeId, val, playbackSpeedMs);
    };

    const handleSpeedChange = (val) => {
        setPlaybackSpeedMs(val);
        autoSaveConfig(screenId, selectedThemeId, sequenceLength, val);
    };

    const handleThemeChange = (val) => {
        setSelectedThemeId(val);
        autoSaveConfig(screenId, val, sequenceLength, playbackSpeedMs);
    };

    /**
     * Resuelve la URL absoluta para recursos multimedia locales o remotos.
     */
    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:') || path.startsWith('data:')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '/');
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        return `${apiBase}${cleanPath}`;
    };

    const activeButtons = gameState.buttons && gameState.buttons.length > 0 
        ? gameState.buttons 
        : customButtonsFromActiveTheme();

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic} getOutOfTheDynamic={getOutOfTheDynamic}>
            {showConfirmModal && (
                <Confirm 
                    message={confirmConfig.message} 
                    actionToYesButton={confirmConfig.action} 
                    textYesButton="Sí, Confirmar" 
                    textNoButton="Cancelar" 
                    setShowModal={setShowConfirmModal} 
                />
            )}

            {showQRModal && (
                <>
                    <div className="cModal__Background" onClick={() => setShowQRModal(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div className="cModal__Container__Content position-relative p-0" style={{ background: 'transparent', boxShadow: 'none' }}>
                            <button 
                                type="button" 
                                className="btn-close position-absolute top-0 end-0 m-3 bg-dark p-2 rounded-circle" 
                                onClick={() => setShowQRModal(false)} 
                                style={{ zIndex: 20 }}
                                aria-label="Cerrar modal de QR"
                            ></button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1"><i className="bi bi-tv"></i> Abre este link en tu TV o pantalla de proyección:</span>
                                <span className="fw-bold user-select-all text-break">{tvTargetUrl}</span>
                            </div>
                            <PremiumQR url={qrTargetUrl} title={qrTitle} />
                        </div>
                    </div>
                </>
            )}

            {/* MODAL DE EDICIÓN DE TEMA */}
            {showThemeModal && (
                <>
                    <div className="cModal__Background" style={{ zIndex: 99998 }} onClick={() => setShowThemeModal(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '800px', width: '90vw' }}>
                        <div className="cModal__Container__Content bg-white text-dark p-4 rounded-4 shadow-lg">
                            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                                <h3 className="fw-bold text-primary mb-0">
                                    {themeForm.id === 0 ? '✨ Crear Nuevo Tema' : '✏️ Editar Tema'}
                                </h3>
                                <button type="button" className="btn-close" onClick={() => setShowThemeModal(false)} aria-label="Cerrar modal"></button>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-bold text-muted">Nombre del Tema / Patrocinador</label>
                                <input 
                                    type="text" 
                                    className="form-control form-control-lg fw-bold" 
                                    placeholder="Ej: Tecate, Coca-Cola, Navideño..."
                                    value={themeForm.name}
                                    onChange={(e) => setThemeForm(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>

                            <h5 className="fw-bold text-muted mb-3 border-bottom pb-1">Configuración de los 6 Cuadros</h5>
                            <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '5px' }}>
                                <div className="row g-3">
                                    {themeForm.buttons.map((btn) => (
                                        <div key={btn.id} className="col-md-6">
                                            <div className="p-3 border rounded bg-light">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <span className="badge bg-primary rounded-circle">{btn.id}</span>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-sm fw-bold text-uppercase" 
                                                        placeholder="Etiqueta" 
                                                        value={btn.label || ''} 
                                                        onChange={(e) => updateThemeButtonField(btn.id, 'label', e.target.value)}
                                                    />
                                                </div>

                                                <div className="mb-2">
                                                    <label className="small text-muted fw-bold d-block mb-1">Color de Fondo</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input 
                                                            type="color" 
                                                            className="form-control form-control-color" 
                                                            value={btn.color && btn.color.startsWith('#') ? btn.color : '#ffffff'} 
                                                            onChange={(e) => updateThemeButtonField(btn.id, 'color', e.target.value)}
                                                            style={{ width: '38px', height: '38px', padding: '0', border: '0', borderRadius: '4px' }}
                                                        />
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm" 
                                                            placeholder="#Hex"
                                                            value={btn.color}
                                                            onChange={(e) => updateThemeButtonField(btn.id, 'color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="small text-muted fw-bold d-block mb-1">Imagen del Cuadro</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-dark btn-sm text-nowrap fw-bold"
                                                            onClick={() => document.getElementById(`media-upload-btn-${btn.id}`).click()}
                                                            disabled={isUploading[btn.id]}
                                                        >
                                                            {isUploading[btn.id] ? 'Subiendo...' : <><i className="bi bi-upload"></i> Subir Imagen</>}
                                                        </button>
                                                        <input 
                                                            type="file" 
                                                            id={`media-upload-btn-${btn.id}`} 
                                                            className="d-none" 
                                                            accept="image/*"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) handleUploadMediaForButton(file, btn.id);
                                                            }}
                                                        />
                                                        {btn.imageUrl && (
                                                            <div className="border rounded p-1 bg-white" style={{ position: 'relative' }}>
                                                                <img src={getMediaUrl(btn.imageUrl)} alt="Btn" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
                                                                <button 
                                                                    type="button" 
                                                                    className="btn-close position-absolute top-0 end-0 bg-danger rounded-circle p-1" 
                                                                    style={{ fontSize: '6px', transform: 'translate(50%, -50%)' }}
                                                                    onClick={() => updateThemeButtonField(btn.id, 'imageUrl', '')}
                                                                    aria-label="Eliminar imagen del cuadro"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                <button className="btn btn-outline-secondary fw-bold px-4" onClick={() => setShowThemeModal(false)}>Cancelar</button>
                                <button className="btn btn-primary fw-bold px-4" onClick={handleSaveTheme}>Guardar Tema</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="container py-4 text-dark" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                    <div>
                        <h2 className="fw-black text-uppercase text-primary mb-0">
                            🎮 CONFIGURACIÓN DE SECUENCIAS
                        </h2>
                        <p className="text-muted small mb-0">
                            Crea temas con colores e imágenes, proyecta el juego y monitorea en tiempo real.
                        </p>
                    </div>
                    <span className={`badge rounded-pill px-3 py-2 ${
                        connectionStatus === 'connected' ? 'bg-success' : 'bg-danger'
                    }`}>
                        {connectionStatus === 'connected' ? '● En línea (WS)' : '○ Desconectado'}
                    </span>
                </div>

                {error && (
                    <div className="alert alert-danger border-0 shadow-sm mb-4" role="alert">
                        ⚠️ {error}
                    </div>
                )}

                <div className="row g-4">
                    {/* Panel de Configuración y Parámetros */}
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm mb-4 rounded-4">
                            <div className="card-body p-4">
                                <h4 className="fw-bold mb-4 text-primary"><i className="bi bi-sliders"></i> PARÁMETROS</h4>
                                
                                {/* Selección de Tema */}
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold d-flex justify-content-between">
                                        <span>Seleccionar Tema Activo</span>
                                        {/* <button className="btn btn-link btn-sm p-0 text-decoration-none fw-bold" onClick={openCreateTheme}>+ Crear Nuevo Tema</button> */}
                                    </label>
                                    <div className="d-flex gap-2">
                                        <select 
                                            className="form-select bg-light border-1 p-3 fw-bold text-primary flex-grow-1"
                                            value={selectedThemeId}
                                            onChange={(e) => handleThemeChange(e.target.value)}
                                            style={{ borderRadius: '12px' }}
                                        >
                                            <option value="">-- Tema por Defecto --</option>
                                            {themesList.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                        {selectedThemeId && (
                                            <button 
                                                className="btn btn-outline-secondary px-3" 
                                                onClick={() => openEditTheme(themesList.find(t => t.id === parseInt(selectedThemeId, 10)))}
                                                style={{ borderRadius: '12px' }}
                                                title="Editar este tema"
                                            >
                                                <i className="bi bi-pencil-fill"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Sequence Length */}
                                <div className="mb-4">
                                    <label className="form-label text-muted fw-bold d-flex justify-content-between">
                                        {/* <span>Longitud de la Secuencia (Pasos)</span> */}
                                        <span>¿De cuántos pasos quieres que sea la secuencia?</span>
                                        <span className="text-primary fw-black">{sequenceLength} pasos</span>
                                    </label>
                                    <input 
                                        type="range" 
                                        className="form-range" 
                                        min="3" 
                                        max="12" 
                                        value={sequenceLength}
                                        onChange={(e) => handleLengthChange(parseInt(e.target.value, 10))}
                                        disabled={gameState.isPlaying && gameState.phase !== 'WAITING'}
                                    />
                                    <div className="d-flex justify-content-between text-muted small mt-1">
                                        <span>Fácil (3)</span>
                                        <span>Difícil (12)</span>
                                    </div>
                                </div>

                                {/* Botones de Control Principal en Fila Vertical Ancho Completo */}
                                <div className="d-grid gap-3 pt-2">
                                    {/* 1. Mostrar Portada */}
                                    <button 
                                        className="btn btn-outline-secondary btn-lg fw-bold p-3 w-100 shadow-sm"
                                        onClick={handleShowFrontPageManual}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        🖼️ Mostrar portada
                                    </button>

                                    {/* 2. Proyectar Pantalla de Juego */}
                                    <button 
                                        className="btn btn-outline-primary btn-lg fw-bold p-3 shadow-sm w-100"
                                        onClick={handleProjectGameManual}
                                        style={{ borderRadius: '12px', letterSpacing: '0.5px' }}
                                    >
                                        📺 Proyectar tablero de Juego
                                    </button>

                                    {/* 3. Iniciar Partida */}
                                    <button 
                                        className="btn btn-success btn-lg fw-bold p-3 text-white shadow-sm w-100"
                                        onClick={handleStart}
                                        disabled={isWorking || (gameState.isPlaying && gameState.phase !== 'WAITING')}
                                        style={{ borderRadius: '12px', letterSpacing: '0.5px' }}
                                    >
                                        🚀 Iniciar partida
                                    </button>

                                    {/* 4. Resetear */}
                                    <button 
                                        className="btn btn-outline-danger btn-lg fw-bold p-3 shadow-sm w-100"
                                        onClick={handleReset}
                                        disabled={isWorking}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        🔄 Reiniciar partida
                                    </button>

                                    {/* 5. Instrucciones */}
                                    <button 
                                        className="btn btn-outline-primary btn-lg fw-bold p-3 shadow-sm w-100"
                                        onClick={openQRModal}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        📲 Ver instrucciones de conexión
                                    </button>
                                </div>

                                {/* Sección Plegable: Configuración Avanzada */}
                                <div className="mt-4 pt-3 border-top">
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-secondary w-100 d-flex justify-content-between align-items-center py-2 px-3 fw-bold"
                                        onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                                        style={{ borderRadius: '10px' }}
                                    >
                                        <span><i className="bi bi-gear-fill me-2"></i> Configuración Avanzada</span>
                                        <i className={`bi bi-chevron-${showAdvancedConfig ? 'up' : 'down'}`}></i>
                                    </button>
                                    
                                    {showAdvancedConfig && (
                                        <div className="bg-light p-3 rounded-3 mt-3 border">
                                            {/* Screen Selector */}
                                            <div className="mb-3">
                                                <label className="form-label text-muted fw-bold small">ID de Pantalla (ScreenId)</label>
                                                <select 
                                                    className="form-select bg-white border-1 p-2 fw-bold text-dark"
                                                    value={screenId}
                                                    onChange={(e) => setScreenId(parseInt(e.target.value, 10))}
                                                    disabled={gameState.isPlaying && gameState.phase !== 'WAITING'}
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    <option value={1}>Pantalla 1 (Estadio Principal)</option>
                                                    <option value={2}>Pantalla 2 (Stand Lateral)</option>
                                                    <option value={3}>Pantalla 3 (Stand Entrada)</option>
                                                    <option value={4}>Pantalla 4 (Stand VIP)</option>
                                                </select>
                                            </div>

                                            {/* Playback Speed */}
                                            <div>
                                                <label className="form-label text-muted fw-bold small d-flex justify-content-between">
                                                    <span>Velocidad de Destello</span>
                                                    <span className="text-primary fw-bold">{playbackSpeedMs} ms</span>
                                                </label>
                                                <input 
                                                    type="range" 
                                                    className="form-range" 
                                                    min="300" 
                                                    max="1200" 
                                                    step="50"
                                                    value={playbackSpeedMs}
                                                    onChange={(e) => handleSpeedChange(parseInt(e.target.value, 10))}
                                                    disabled={gameState.isPlaying && gameState.phase !== 'WAITING'}
                                                />
                                                <div className="d-flex justify-content-between text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                                    <span>Rápido (300ms)</span>
                                                    <span>Lento (1200ms)</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Listado de Temas Creados */}
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="fw-bold mb-0 text-primary"><i className="bi bi-journal-album"></i> TEMAS DISPONIBLES</h4>
                                    <button className="btn btn-primary btn-sm fw-bold px-3" onClick={openCreateTheme} style={{ borderRadius: '8px' }}>
                                        <i className="bi bi-plus-circle-fill"></i> Crear Tema
                                    </button>
                                </div>
                                {themesList.length === 0 ? (
                                    <div className="text-center py-3 text-muted small">No hay temas personalizados. Crea uno.</div>
                                ) : (
                                    <div className="list-group">
                                        {themesList.map(t => (
                                            <div key={t.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center rounded-3 mb-2 border border-light shadow-sm">
                                                <div>
                                                    <span className="fw-bold text-dark">{t.name}</span>
                                                    <span className="text-muted small d-block">ID: {t.id} • Creado el {new Date(t.createdAt).toLocaleDateString()}</span>
                                                </div>
                                                <div className="btn-group">
                                                    <button className="btn btn-outline-primary btn-sm fw-bold" onClick={() => openEditTheme(t)} title="Editar tema"><i className="bi bi-pencil-square"></i></button>
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteThemeClick(t.id)} title="Eliminar tema"><i className="bi bi-trash-fill"></i></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Monitoreo en Vivo */}
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm mb-4 rounded-4">
                            <div className="card-body p-4">
                                <h4 className="fw-bold mb-4 text-primary"><i className="bi bi-display"></i> MONITOREO EN VIVO</h4>
                                
                                {/* Estado Actual */}
                                <div className="mb-4 text-center">
                                    <span className={`fs-5 fw-bold px-4 py-2 rounded-3 text-uppercase d-inline-block shadow-sm ${
                                        gameState.phase === 'WAITING' ? 'bg-secondary text-white' :
                                        gameState.phase === 'PLAYBACK' ? 'bg-warning text-dark animate-pulse' :
                                        gameState.phase === 'PLAYER_INPUT' ? 'bg-primary text-white' :
                                        gameState.phase === 'WON' ? 'bg-success text-white' :
                                        'bg-danger text-white'
                                    }`}>
                                        {gameState.phase === 'WAITING' && '⏳ Esperando inicio'}
                                        {gameState.phase === 'PLAYBACK' && '📺 Reproduciendo secuencia'}
                                        {gameState.phase === 'PLAYER_INPUT' && '📱 Turno del aficionado'}
                                        {gameState.phase === 'WON' && '🏆 ¡SECUENCIA CORRECTA!'}
                                        {gameState.phase === 'LOST' && '❌ ¡SECUENCIA FALLIDA!'}
                                    </span>
                                </div>

                                {/* Secuencia Generada */}
                                {gameState.sequence && gameState.sequence.length > 0 && (
                                    <div className="mb-4">
                                        <label className="text-muted fw-bold d-block mb-2">Secuencia del Juego</label>
                                        <div className="bg-light p-3 rounded-3 border d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
                                            {gameState.sequence.map((colorId, i) => {
                                                const btnConfig = activeButtons.find(b => b.id === colorId) || { color: '#888', label: '?' };
                                                const isCurrent = gameState.phase === 'PLAYER_INPUT' && i === gameState.playerStep;
                                                return (
                                                    <div key={i} className="d-flex align-items-center">
                                                        <span 
                                                            className="rounded-circle d-inline-block border"
                                                            style={{ 
                                                                width: '24px', 
                                                                height: '24px', 
                                                                backgroundColor: btnConfig.color,
                                                                transform: isCurrent ? 'scale(1.2)' : 'none',
                                                                boxShadow: isCurrent ? '0 0 10px rgba(0,0,0,0.5)' : 'none'
                                                            }}
                                                            title={btnConfig.label}
                                                        />
                                                        {i < gameState.sequence.length - 1 && (
                                                            <span className="text-muted mx-1">→</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Progreso */}
                                {gameState.phase === 'PLAYER_INPUT' && (
                                    <div className="mb-4">
                                        <label className="text-muted fw-bold d-block mb-1">Progreso del Jugador</label>
                                        <div className="progress bg-light border" style={{ height: '24px', borderRadius: '12px' }}>
                                            <div 
                                                className="progress-bar progress-bar-striped progress-bar-animated bg-info" 
                                                role="progressbar" 
                                                style={{ width: `${gameState.sequence?.length ? (gameState.playerStep / gameState.sequence.length) * 100 : 0}%` }}
                                            >
                                                <span className="fw-black text-dark fs-6">{gameState.playerStep} / {gameState.sequence?.length || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Controles de Emergencia */}
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <h4 className="fw-bold mb-2 text-danger"><i className="bi bi-cone-striped"></i> CONTROLES DE EMERGENCIA</h4>
                                <p className="text-muted small mb-4">
                                    Haz clic en estos botones para ingresar toques manualmente si falla el celular del aficionado.
                                </p>
                                
                                <div className="row g-3">
                                    {activeButtons.map((btn) => (
                                        <div key={btn.id} className="col-4">
                                            <button
                                                type="button"
                                                className="btn w-100 py-3 border-2 shadow-sm fw-bold d-flex flex-column align-items-center justify-content-center"
                                                onClick={() => handleEmergencyTap(btn.id)}
                                                disabled={gameState.phase !== 'PLAYER_INPUT'}
                                                style={{
                                                    borderColor: btn.color,
                                                    background: 'white',
                                                    color: 'black',
                                                    borderRadius: '16px',
                                                    opacity: gameState.phase !== 'PLAYER_INPUT' ? 0.6 : 1
                                                }}
                                            >
                                                {btn.imageUrl ? (
                                                    <img src={getMediaUrl(btn.imageUrl)} alt={btn.label} style={{ width: '32px', height: '32px', objectFit: 'contain', marginBottom: '5px' }} />
                                                ) : (
                                                    <span className="rounded-circle d-inline-block mb-2 border" style={{ width: '20px', height: '20px', backgroundColor: btn.color }} />
                                                )}
                                                <span className="small text-uppercase text-truncate w-100">{btn.label || `Palo ${btn.id}`}</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Historial de Partidas */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="fw-bold mb-0 text-primary"><i className="bi bi-clock-history"></i> HISTORIAL DE PARTIDAS</h4>
                                    {history.length > 0 && (
                                        <button className="btn btn-outline-danger btn-sm fw-bold" onClick={handleClearHistory}>
                                            <i className="bi bi-trash"></i> Limpiar Historial
                                        </button>
                                    )}
                                </div>

                                {history.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        No hay registros de partidas jugadas aún.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Partida ID</th>
                                                    <th>Pantalla</th>
                                                    <th>Pasos Totales</th>
                                                    <th>Pasos Logrados</th>
                                                    <th>Resultado</th>
                                                    <th>Fecha</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {history.map((h) => (
                                                    <tr key={h.id || h.matchId || Math.random()}>
                                                        <td><code className="text-secondary">{h.matchId}</code></td>
                                                        <td>Pantalla {h.screenId}</td>
                                                        <td>{h.sequenceLength}</td>
                                                        <td>{h.playerStep}</td>
                                                        <td>
                                                            <span className={`badge ${h.hasWon ? 'bg-success' : 'bg-danger'} px-3 py-2`}>
                                                                {h.hasWon ? 'GANÓ' : 'PERDIÓ'}
                                                            </span>
                                                        </td>
                                                        <td>{h.playedAt ? new Date(h.playedAt).toLocaleString() : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};

Sequence.propTypes = {
    getOutOfTheDynamic: PropTypes.func.isRequired,
    roomId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Sequence.defaultProps = {
    roomId: null
};

export { Sequence };
export default Sequence;
